import React, { useContext, useMemo, useState } from "react";

import { makeStyles } from "tss-react/mui";
import {
  Card,
  CardActionArea,
  Dialog,
  GlobalStyles,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";

import { RetroCard } from "components/retrospective/RetroCard";
import { RetroContext } from "components/retrospective/RetrospectiveMain";
import { IosShare } from "@mui/icons-material";

const useStyles = makeStyles()((theme) => ({
  outer: {
    flex: 1,
    display: "grid",
    height: "100%",
    width: "100%",
    gap: theme.spacing(3),

    // vertical
    gridTemplateRows: "repeat(6, 1fr)",
    gridTemplateColumns: "repeat(2, 1fr)",

    // horizontal
    "@media (orientation: landscape)": {
      gridTemplateRows: "repeat(4, 1fr)",
      gridTemplateColumns: "40% 1fr 1fr",
    },
  },
  textArea: {
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",

    "& :first-child": {
      fontSize: "40px",
      lineHeight: "50px",
      background:
        "linear-gradient(90deg, #7BF1A8 0%, #00D492 50%, #46ECD5 100%)",
      backgroundClip: "text",
      color: "transparent",
      textAlign: "center",
    },

    //vertical
    gridColumn: "span 2",
    gridRow: "span 1",

    // horizontal
    "@media (orientation: landscape)": {
      gridColumn: "span 3",
      gridRow: "span 1",
    },
  },
  imgArea: {
    alignItems: "center",
    justifyContent: "center",

    //vertical
    gridColumn: "span 2",
    gridRow: "span 2",

    // horizontal
    "@media (orientation: landscape)": {
      gridColumn: "span 1",
      gridRow: "span 2",
    },
  },
  footerArea: {
    alignItems: "center",
    justifyContent: "center",

    //vertical
    gridColumn: "span 2",
    gridRow: "span 1",

    // horizontal
    "@media (orientation: landscape)": {
      gridColumn: "span 3",
      gridRow: "span 1",
    },
  },
  dialog: {
    width: "60vw",
    maxWidth: "300px",
    height: "50vh",
    maxHeight: "500px",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(3),
    padding: theme.spacing(3),
  },
  card: {
    flex: 1,
    width: "100%",

    "& > button": {
      height: "100%",
      display: "flex",
      flexDirection: "column",
      flex: 1,
      alignItems: "center",
      justifyContent: "space-around",
    },
  },
}));

const SHARE_DATA = {
  title: "Retrospectiva Enermatch",
  text: "Descubra quanto sua escola economizou esse ano",
  url: "https://enermatch.com/retrospectiva2025",
};

const bgGradient =
  "linear-gradient(135deg, #096 0%, #009689 50%, #0092B8 100%)";

const FETCH_CONFIG = {
  headers: {
    "Content-Type": "image/png",
  },
};

export const RetroConclusion = () => {
  const { classes } = useStyles();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { salasClimatizadas, co2EvitadoTotal, percentualEnergiaVerde, id } =
    useContext(RetroContext);

  const canShare = useMemo(
    () => !!(navigator.canShare && navigator.canShare(SHARE_DATA)),
    []
  );
  const feedLink = useMemo(
    () =>
      `https://retrospective-2025.s3.us-east-1.amazonaws.com/${id}/feed_6.png`,
    [id]
  );
  const storyLink = useMemo(
    () =>
      `https://retrospective-2025.s3.us-east-1.amazonaws.com/${id}/story_6.png`,
    [id]
  );

  const handleShareButton = async () => {
    setShowDialog(true);
  };

  const handleShare = async (fileLink) => {
    try {
      const imgBlob = await fetch(fileLink, FETCH_CONFIG).then((res) =>
        res.blob()
      );
      const data = {
        ...SHARE_DATA,
        files: [
          new File([imgBlob], "retrospectiva", {
            type: imgBlob.type,
          }),
        ],
      };
      await navigator.share(data);
    } catch (err) {
      window.open(fileLink);
    }
  };

  return (
    <Grid item container className={classes.outer}>
      <GlobalStyles
        styles={{
          body: {
            background: `center / cover no-repeat url(/static/img/retro2025/yourImpact.jpg)`,
          },
        }}
      />
      <Grid item container className={classes.textArea}>
        <Typography variant="h1">Retrospectiva 2025</Typography>
        <Typography variant="h2" color="common.white">
          Seu impacto ao longo do ano
        </Typography>
      </Grid>
      <Grid item container className={classes.imgArea}>
        <img height="90%" alt="" src="/static/img/retro2025/co2Img.png" />
      </Grid>
      <RetroCard
        mainInfo={co2EvitadoTotal?.toLocaleString("pt-br") || " - "}
        caption="CO₂e evitado total"
        gridColumnVertical="span 2"
        gridRowVertical="span 1"
        variant="light"
        gridColumnHorizontal="span 2"
        gridRowHorizontal="span 1"
      />
      <RetroCard
        mainInfo={
          `${percentualEnergiaVerde?.toLocaleString("pt-br")}%` || " - "
        }
        caption="Consumo de energia verde"
        gridColumnVertical="span 1"
        gridRowVertical="span 1"
        /* todo */
        variant="light"
        gridColumnHorizontal="span 1"
        gridRowHorizontal="span 1"
      />
      <RetroCard
        mainInfo={`${salasClimatizadas?.toLocaleString("pt-br")}` || " - "}
        caption="Salas climatizadas"
        gridColumnVertical="span 1"
        gridRowVertical="span 1"
        /* todo */
        variant="light"
        gridColumnHorizontal="span 1"
        gridRowHorizontal="span 1"
      />
      <Grid item container className={classes.footerArea} gap={1}>
        <img height="20px" src="/static/img/retro2025/miniLogo.png" alt="" />

        <Typography color="common.white" variant="caption" fontWeight="bold">
          enermatch.com/retrospectiva2025
        </Typography>

        <IconButton sx={{ color: "white" }} onClick={handleShareButton}>
          <IosShare />
        </IconButton>
        <Dialog open={showDialog} onClose={() => setShowDialog(false)}>
          <Grid
            item
            container
            className={classes.dialog}
            sx={{ background: bgGradient }}
          >
            <Card className={classes.card}>
              <CardActionArea
                onClick={() => {
                  canShare ? handleShare(feedLink) : window.open(feedLink);
                }}
              >
                <Typography variant="body2">Formato Feed</Typography>
                <Grid
                  sx={{
                    height: "100px",
                    width: "100px",
                    borderRadius: "18px",
                    background: bgGradient,
                  }}
                />
              </CardActionArea>
            </Card>
            <Card className={classes.card}>
              <CardActionArea
                onClick={() => {
                  canShare ? handleShare(storyLink) : window.open(storyLink);
                }}
              >
                <Typography variant="body2">Formato Story</Typography>
                <Grid
                  sx={{
                    height: "100px",
                    width: "60px",
                    borderRadius: "18px",
                    background: bgGradient,
                  }}
                />
              </CardActionArea>
            </Card>
          </Grid>
        </Dialog>
      </Grid>
    </Grid>
  );
};
